import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  Background,
  Controls,
  MiniMap,
} from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";
import { v4 as uuid } from "uuid";
import {
  Box,
  Button,
  Popover,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Title from "../../../../../components/Title";
import {
  useCreateTagType,
  useUpdateTagTree,
  useTagType,
} from "../../../../../api/tags/tags";
import { useSnackbar } from "../../../../../context/SnackbarContext";

interface TreeNode {
  id: string | number;
  label: string;
  children: TreeNode[];
}

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));
const nodeWidth = 150;
const nodeHeight = 50;

const getLayoutedElements = (nodes, edges, direction = "TB") => {
  dagreGraph.setGraph({ rankdir: direction });
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });
  dagre.layout(dagreGraph);

  let minX = Infinity,
    minY = Infinity;
  nodes.forEach((node) => {
    const { x, y } = dagreGraph.node(node.id);
    if (x < minX) minX = x;
    if (y < minY) minY = y;
  });

  const centerOffsetX = 400 - minX;
  const centerOffsetY = 200 - minY;

  const layoutedNodes = nodes.map((node) => {
    const { x, y } = dagreGraph.node(node.id);
    return {
      ...node,
      position: { x: x + centerOffsetX, y: y + centerOffsetY },
    };
  });
  return { nodes: layoutedNodes, edges };
};

const apiNodeToTreeNode = (node) => ({
  id: node.id.toString(),
  label: node.name,
  children: (node.children || []).map(apiNodeToTreeNode),
});

export default function MindMapBuilder() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const id = params.get("id");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { data: editTreeRaw, isLoading } = useTagType(id, { enabled: !!id });
  const { showSnackbar } = useSnackbar();

  const isEditMode = !!id;

  const productCounterRef = useRef(2);
  const subsystemCounterRef = useRef(1);
  const componentCounterRef = useRef(1);

  const [tree, setTree] = useState<TreeNode[]>(
    isEditMode ? [] : [{ id: uuid(), label: "product", children: [] }]
  );

  useEffect(() => {
    if (isEditMode && editTreeRaw) {
      const nextTree = Array.isArray(editTreeRaw)
        ? editTreeRaw.map(apiNodeToTreeNode)
        : [apiNodeToTreeNode(editTreeRaw)];
      setTree(nextTree);
    }
    if (!isEditMode && tree.length === 0) {
      setTree([{ id: uuid(), label: "product", children: [] }]);
    }
  }, [editTreeRaw, isEditMode]);

  useEffect(() => {
    const allNodes = [];
    const allEdges = [];
    const walk = (node, parent = null) => {
      allNodes.push({
        id: node.id.toString(),
        data: { label: node.label },
        position: { x: 0, y: 0 },
      });
      if (parent)
        allEdges.push({
          id: `e-${parent}-${node.id}`,
          source: parent.toString(),
          target: node.id.toString(),
          animated: true,
        });
      (node.children || []).forEach((child) => walk(child, node.id));
    };
    tree.forEach((root) => walk(root));

    const { nodes: ln, edges: le } = getLayoutedElements(
      allNodes,
      allEdges,
      "TB"
    );
    setNodes(ln);
    setEdges(le);
  }, [tree, setNodes, setEdges]);

  const findNode = (
    arr: TreeNode[],
    id: string | number,
    depth = 0
  ): [TreeNode | null, number] => {
    for (const n of arr) {
      if (n.id === id) return [n, depth];
      const [found, d] = findNode(n.children, id, depth + 1);
      if (found) return [found, d];
    }
    return [null, -1];
  };

  const updateTree = (id: string, fn: (n: TreeNode) => void) => {
    const recurse = (arr: TreeNode[]): TreeNode[] =>
      arr.map((n) => {
        if (n.id === id) fn(n);
        else n.children = recurse(n.children);
        return n;
      });
    setTree(recurse(tree));
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(
    null
  );
  const [editLabel, setEditLabel] = useState("");

  const onNodeClick = useCallback(
    (event: any, { id }: any) => {
      event.preventDefault();
      const [node] = findNode(tree, id);
      if (!node) return;
      setSelectedId(id);
      setEditLabel(node.label);
      setAnchor({ top: event.clientY, left: event.clientX });
    },
    [tree]
  );

  const closePopover = () => setSelectedId(null) || setAnchor(null);

  const saveEdit = () => {
    if (!selectedId) return;
    updateTree(selectedId, (n) => (n.label = editLabel));
    setNodes((ns) =>
      ns.map((n) =>
        n.id === selectedId ? { ...n, data: { label: editLabel } } : n
      )
    );
    closePopover();
  };

  const deleteNode = () => {
    if (!selectedId) return;
    const filterTree = (arr: TreeNode[]): TreeNode[] =>
      arr
        .filter((n) => n.id !== selectedId)
        .map((n) => ({ ...n, children: filterTree(n.children) }));
    setTree(filterTree(tree));
    setNodes((ns) => ns.filter((n) => n.id !== selectedId));
    setEdges((es) =>
      es.filter((e) => e.source !== selectedId && e.target !== selectedId)
    );
    closePopover();
  };

  const addChild = () => {
    if (!selectedId) return;
    const [node, depth] = findNode(tree, selectedId);
    if (!node || depth < 0 || depth >= 2) return;
    const id = uuid();
    let label = "";
    if (depth === 0) {
      label = `subsystem_${subsystemCounterRef.current++}`;
    } else if (depth === 1) {
      label = `component_${componentCounterRef.current++}`;
    }
    const newNode: TreeNode = { id, label, children: [] };
    updateTree(selectedId, (n) => n.children.push(newNode));
    setNodes((ns) => [
      ...ns,
      { id, data: { label }, position: { x: 0, y: 0 } },
    ]);
    setEdges((es) => [
      ...es,
      {
        id: `e-${selectedId}-${id}`,
        source: selectedId,
        target: id,
        animated: true,
      },
    ]);
    closePopover();
  };

  const addRoot = () => {
    const id = uuid();
    const label = `product_${productCounterRef.current++}`;
    const newRoot: TreeNode = { id, label, children: [] };
    setTree((prev) => [...prev, newRoot]);
    setNodes((ns) => [
      ...ns,
      { id, data: { label }, position: { x: 0, y: 0 } },
    ]);
  };

  const createTagType = useCreateTagType();

  const updateTagTree = useUpdateTagTree();
  async function saveTree(nodes, parent = null) {
    for (const node of nodes) {
      const created = await createTagType.mutateAsync({
        name: node.label,
        parent: parent,
      });
      if (node.children && node.children.length > 0) {
        await saveTree(node.children, created.id);
      }
    }
  }
  const handleSave = async () => {
    try {
      if (isEditMode) {
        await updateTagTree.mutateAsync({ id, tree });
      } else {
        await saveTree(tree, null);
      }
      navigate(-1);
      showSnackbar("Product tag type created successfully!", "success");
    } catch (e) {
      showSnackbar(
        e?.response?.data?.message || "An error occurred while saving.",
        "error"
      );
    }
  };
  if (isEditMode && isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ReactFlowProvider>
      <Title>{isEditMode ? "Edit Product" : "Create Product"}</Title>
      <Box
        sx={{
          width: "100%",
          height: "65vh",
          border: "1px solid #ddd",
          borderRadius: 2,
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onConnect={(p) =>
            setEdges((es) => addEdge({ ...p, animated: true }, es))
          }
          fitView
        >
          <Background gap={20} />
          <MiniMap />
          <Controls />
        </ReactFlow>
      </Box>

      <Popover
        open={!!anchor}
        anchorReference="anchorPosition"
        anchorPosition={anchor!}
        onClose={closePopover}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box p={2} sx={{ minWidth: 260 }}>
          {selectedId && (
            <>
              <Typography variant="h6">Edit Node</Typography>
              <TextField
                label="Label"
                fullWidth
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                sx={{ my: 1 }}
                autoFocus
              />
              <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={deleteNode}
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={saveEdit}
                  startIcon={<AddIcon />}
                  sx={{ fontWeight: 700 }}
                >
                  Save
                </Button>
              </Box>
              <Button
                fullWidth
                startIcon={<AddIcon />}
                onClick={addChild}
                variant="text"
                sx={{ mt: 1 }}
              >
                Add Child
              </Button>
            </>
          )}
        </Box>
      </Popover>

      <Box display="flex" justifyContent="space-between" p={2} gap={2}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRoot}
          disabled={nodes.length > 0}
        >
          Add Product
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          sx={{
            fontWeight: 700,
            fontSize: 18,
            px: 5,
            py: 1.5,
            boxShadow: "0 2px 8px 0 #66bb6a88",
            textTransform: "none",
          }}
        >
          {isEditMode ? "Save Changes" : "Create"}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    </ReactFlowProvider>
  );
}
